import {Accordion, AccordionDetails, AccordionSummary, Card, Grid, Typography} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {SwitchSegmentedYesNo, TextBox} from '../../../Core/UI';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import {SelectInputElement} from '../Components/SelectInputElement';
import React from 'react';
import {observer} from 'mobx-react-lite';
import {HealthQuestionsProps} from './HealthQuestionsList';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/HealthXref';

const HealthRelated = observer(({counter, valueChanged, parameters, coreQuestions}: HealthQuestionsProps) => {
    return (
        <Accordion style={{width: '100%'}} TransitionProps={{unmountOnExit: true}}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={'health-content'} id={'health-header'}>
                <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    <div>Health</div>
                    <div>{counter('health')}</div>
                </div>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container>
                    <Grid key="params" container>
                        {coreQuestions?.map(({name, label}) => (
                            <Grid item xs={4} key={name}>
                                <SwitchSegmentedYesNo
                                    param={{
                                        name: name,
                                        label: label,
                                        category: 'health',
                                        ...(parameters[name as keyof typeof parameters] as Parameter),
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                />
                            </Grid>
                        ))}
                        {parameters.Herpes.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Rheumatoid Arthritis</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%'}}
                                                    value={parameters.herpesDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.herpesDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'herpesPendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital?',
                                                    ...parameters.herpesPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.KidneyStones.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Kidney Stones</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.kidneystonesDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.kidneystonesDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'kidneystonesResolved',
                                                    label: 'Are the Kidney Stones Present, Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?',
                                                    ...parameters.kidneystonesResolved,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Insomnia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Insomnia</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.insomniaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.insomniaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="insomniaResults"
                                                label="Have you consulted with any other physician for this other than your personal physician?"
                                                value={parameters.insomniaResults?.value}
                                                onChange={(value) => valueChanged(parameters.insomniaResults, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Pneumonia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Pneumonia</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.pneumoniaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.pneumoniaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'pneumoniaTreatmentCompleted',
                                                    label: 'Are you currently under treatment or experiencing symptoms related to this condition?',
                                                    ...parameters.pneumoniaTreatmentCompleted,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="parkinsonsSymptoms"
                                                label="Provide details of your treatment and symptoms., Provide details of your complications."
                                                value={parameters.parkinsonsSymptoms?.value}
                                                onChange={(value) => valueChanged(parameters.parkinsonsSymptoms, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.BloodClotinthelungsPulmonaryEmbolism.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Blood Clot in the lungs (Pulmonary Embolism)</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.pulmonaryembolismDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.pulmonaryembolismDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'pulmonaryembolismSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.pulmonaryembolismSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="pulmonaryembolismTreatment"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.pulmonaryembolismTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.pulmonaryembolismTreatment, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Bronchitis.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Bronchitis</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.bronchitisDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.bronchitisDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'bronchitisChronic',
                                                    label: 'Were you diagnosed with Chronic Bronchitis?',
                                                    ...parameters.bronchitisChronic,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'bronchitisEpisodes',
                                                    label: 'Have you had more than two episodes in the last 12 months?',
                                                    ...parameters.bronchitisEpisodes,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'bronchitisRecovery',
                                                    label: 'Have you had a full recovery with no complications?',
                                                    ...parameters.bronchitisRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'bronchitisSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.bronchitisSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="bronchitisTreatment"
                                                label="Provide details of your symptoms and treatment received."
                                                value={parameters.bronchitisTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.bronchitisTreatment, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.UTIUrinaryTractInfection.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>UTI (Urinary Tract Infection)</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.utiDiagnosisDate?.value || null}
                                                    onChange={(date) => valueChanged(parameters.utiDiagnosisDate, date)}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'utiPendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?',
                                                    ...parameters.utiPendingDiagnsois,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'utiEpisodes',
                                                    label: 'Have you had more than one episode of this condition?',
                                                    ...parameters.utiEpisodes,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'utiRecovery',
                                                    label: 'Have you had a full recovery with no complications?',
                                                    ...parameters.utiRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Sexuallytransmitteddiseases.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Sexually Transmitted Diseases</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.stdDiagnosisDate?.value || null}
                                                    onChange={(date) => valueChanged(parameters.stdDiagnosisDate, date)}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Conjuctiva.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Conjunctiva</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.conjuctivaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.conjuctivaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.MuscleSpasms.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Muscle Spasms</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.musclespasmsDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.musclespasmsDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'musclespasmsPendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?',
                                                    ...parameters.musclespasmsPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'musclespasmsSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.musclespasmsSypmtoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'musclespasmsContinualSymptoms',
                                                    label: 'Have your symptoms persisted for longer than 60 days?',
                                                    ...parameters.musclespasmsContinualSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.StaphInfection.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Staph Infection</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.staphDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.staphDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.CoronavirusCovid19.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Coronavirus / Covid-19</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.covidDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.covidDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Eczema.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Eczema</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'eczemaDiagnosis',
                                                    label: 'Are you diagnosed with Eczema?',
                                                    ...parameters.eczemaDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.eczemaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.eczemaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="exzemaTreatment"
                                                label="Are you currently experiencing symptoms related to this condition?, Have you had a full recovery with no complications?, Provide details of your complications."
                                                value={parameters.exzemaTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.exzemaTreatment, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.HIVAIDS.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>HIV / AIDS</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'hivDiagnosis',
                                                    label: 'Where you diagnosed with HIV or AIDS?',
                                                    ...parameters.hivDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hivDiagnosisDate"
                                                label="When were you diagnosed?"
                                                value={parameters.hivDiagnosisDate?.value}
                                                onChange={(value) => valueChanged(parameters.hivDiagnosisDate, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hivTreatment"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.hivTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.hivTreatment, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Glaucoma.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Glaucoma</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'glaucomaDiagnosis',
                                                    label: 'Are you diagnosed with Glaucoma?',
                                                    ...parameters.glaucomaDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.glaucomaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.glaucomaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="glaucomaTreatment"
                                                label="Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?, Provide details on all pending evaluations."
                                                value={parameters.glaucomaTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.glaucomaTreatment, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.OpioidAddiction.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Opioid Addiction</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="opiodSobriertyYears"
                                                label="How long have you been sober?"
                                                value={parameters.opiodSobriertyYears?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.opiodSobriertyYears, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'opiodaddictionAAMember',
                                                    label: 'Are you currently an active member of AA (Alcoholics Anonymous) or NA (Narcotics Anonymous)?',
                                                    ...parameters.opiodaddictionAAMember,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.AllergiesHayFever.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Allergies / Hay Fever</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'allergiesSeasonality',
                                                    label: 'Are they seasonal allergies?',
                                                    ...parameters.allergiesSeasonality,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'What is the severity?',
                                                    name: 'allergiesSeverity',
                                                    options: fieldOptions.allergiesSeverity.options,
                                                    ...parameters.allergiesSeverity,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'allergiesHospitalizations',
                                                    label: 'In the past 2 years, have you been hospitalized due to allergies or experienced a life-threatening allergic reaction?',
                                                    ...parameters.allergiesHospitalizations,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="allergiesTreatmentPlan"
                                                label="Provide details to include date of hospitalization or life-threatening episode and treatment received."
                                                value={parameters.allergiesTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.allergiesTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.YeastInfection.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Yeast Infection</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.yeastinfectionDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.yeastinfectionDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.PainMedication.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="painmedicationNumber"
                                                label="How many pain medications are you currently taking?"
                                                value={parameters.painmedicationNumber?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.painmedicationNumber, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'painmedicationEmployment',
                                                    label: 'Are you currently employed full time?',
                                                    ...parameters.painmedicationEmployment,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.MuscleRelaxant.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    style={{width: '100%', marginTop: 8}}
                                                    label="When were you diagnosed?"
                                                    value={parameters.musclerelaxantDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.musclerelaxantDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.HeadachesMigraines.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Headaches / Migraines</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    style={{width: '100%', marginTop: 8}}
                                                    label="When were you diagnosed?"
                                                    value={parameters.migraineDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.migraineDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'migrainePendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition?',
                                                    ...parameters.migrainePendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="migrainesSideEffects"
                                                label="Were your headaches severe enough to require medical attention or investigation?"
                                                value={parameters.migrainesSideEffects?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.migrainesSideEffects, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.HBVHepatitisBVirus.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">HBV (Hepatitis B Virus)</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'hepbDiagnosis',
                                                    label: 'Have you been diagnosed with Hep B?',
                                                    ...parameters.hepbDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    style={{width: '100%', marginTop: 8}}
                                                    label="When were you diagnosed?"
                                                    value={parameters.hepbDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.hepbDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'hepbLiverBiopsy',
                                                    label: 'Was a liver biopsy performed?',
                                                    ...parameters.hepbLiverBiopsy,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hepBTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.hepBTreatmentPlan?.value}
                                                onChange={(value) => valueChanged(parameters.hepBTreatmentPlan, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'hepbUltrasound',
                                                    label: 'Was an ultrasound, CT scan or MRI completed for this condition?',
                                                    ...parameters.hepbUltrasound,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.HCVHepatitisCVirus.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">HCV (Hepatitis C Virus)</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'hepcDiagnosis',
                                                    label: 'Have you been diagnosed with Hep C?',
                                                    ...parameters.hepcDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    style={{width: '100%', marginTop: 8}}
                                                    label="When were you diagnosed?"
                                                    value={parameters.hepcDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.hepcDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'hepcLiverBiopsy',
                                                    label: 'Was a liver biopsy performed?',
                                                    ...parameters.hepcLiverBiopsy,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hepcTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.hepcTreatmentPlan?.value}
                                                onChange={(value) => valueChanged(parameters.hepcTreatmentPlan, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'hepcUltrasound',
                                                    label: 'Was an ultrasound, CT scan or MRI completed for this condition?',
                                                    ...parameters.hepcUltrasound,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Anemia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Anemia</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    style={{width: '100%', marginTop: 8}}
                                                    label="When were you diagnosed?"
                                                    value={parameters.anemiaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.anemiaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'anemiaPendingDiagnosis',
                                                    label: 'Apart from your regular follow ups for this condition are you awaiting further medical tests, investigations or test results?',
                                                    ...parameters.anemiaPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="anemiaTreatmentPlan"
                                                label="Provide details on all pending evaluations and treatment related to this condition., What treatment was prescribed or completed for this condition?"
                                                value={parameters.anemiaTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.anemiaTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Menopause.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Menopause</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    style={{width: '100%', marginTop: 8}}
                                                    label="When were you diagnosed?"
                                                    value={parameters.menopauseDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.menopauseDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.TuberculosisTB.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Tuberculosis (TB)</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    style={{width: '100%', marginTop: 8}}
                                                    label="When were you diagnosed?"
                                                    value={parameters.tuberculosisDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.tuberculosisDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'tuberculosisTreatmentCompleted',
                                                    label: 'Have you completed treatment?',
                                                    ...parameters.tuberculosisTreatmentCompleted,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.LeftBundleBranchBlock.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Left Bundle Branch Block</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    style={{width: '100%', marginTop: 8}}
                                                    label="When were you diagnosed?"
                                                    value={parameters.lbbbDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.lbbbDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    style={{width: '100%', marginTop: 8}}
                                                    label="Provide date ablation was completed:"
                                                    value={parameters.lbbbAblationDate?.value || null}
                                                    onChange={(date) => valueChanged(parameters.lbbbAblationDate, date)}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'lbbbSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.lbbbSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="lbbbTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition? Provide details to include the type of surgery and date it was completed (mm/yy)."
                                                value={parameters.lbbbTreatmentPlan?.value}
                                                onChange={(value) => valueChanged(parameters.lbbbTreatmentPlan, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.RightBundleBranchBlock.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Right Bundle Branch Block</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    style={{width: '100%', marginTop: 8}}
                                                    label="When were you diagnosed?"
                                                    value={parameters.rbbbDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.rbbbDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    style={{width: '100%', marginTop: 8}}
                                                    label="Provide date ablation was completed:"
                                                    value={parameters.rbbbAblationDate?.value || null}
                                                    onChange={(date) => valueChanged(parameters.rbbbAblationDate, date)}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'rbbbSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.rbbbSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="rbbbTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition? Provide details to include the type of surgery and date it was completed (mm/yy)."
                                                value={parameters.rbbbTreatmentPlan?.value}
                                                onChange={(value) => valueChanged(parameters.rbbbTreatmentPlan, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Diarrhea.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Diarrhea</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    value={parameters.diarrheaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.diarrheaDiagnosisDate, date)
                                                    }
                                                    style={{width: '100%', marginTop: 8}}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'diarrheaPendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?',
                                                    ...parameters.diarrheaPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="diarrheaTreatmentPlan"
                                                label="Provide details on all pending evaluations and treatment related to this condition. Was this condition caused by a common stomach flu, food poisoning or change in your diet? Provide details to include the cause of the condition and treatment received."
                                                value={parameters.diarrheaTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.diarrheaTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Meningitis.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Meningitis</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    value={parameters.meningitisDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.meningitisDiagnosisDate, date)
                                                    }
                                                    style={{width: '100%', marginTop: 8}}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.AddisonsDisease.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Addison's Disease</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    value={parameters.addisondiseaseDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.addisondiseaseDiagnosisDate, date)
                                                    }
                                                    style={{width: '100%', marginTop: 8}}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'addisondiseasePendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?',
                                                    ...parameters.addisondiseasePendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'addisondiseaseSymptoms',
                                                    label: 'Are you currently experiencing any symptoms or complications due to this condition?',
                                                    ...parameters.addisondiseaseSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="addisondiseaseTreatmentPlan"
                                                label="When did your treatment begin for this condition? Provide details on all pending evaluations and treatment related to this condition."
                                                value={parameters.addisondiseaseTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.addisondiseaseTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.AdrenogenitalSyndrome.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Adrenogenital Syndrome</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    value={parameters.adrenogenitalsyndromeDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(
                                                            parameters.adrenogenitalsyndromeDiagnosisDate,
                                                            date,
                                                        )
                                                    }
                                                    style={{width: '100%', marginTop: 8}}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Bulimia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Bulimia</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    value={parameters.bulimiaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.bulimiaDiagnosisDate, date)
                                                    }
                                                    style={{width: '100%', marginTop: 8}}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="bulimiaTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.bulimiaTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.bulimiaTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'bulimiaCounseling',
                                                    label: 'Have you ever been referred for or received inpatient psychotherapy or counseling?',
                                                    ...parameters.bulimiaCounseling,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'bulimiaHospitalization',
                                                    label: 'Have you ever been hospitalized due to this condition, attempted suicide or had suicidal thoughts?',
                                                    ...parameters.bulimiaHospitalization,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.OCDObsessiveCompulsiveDisorder.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">OCD (Obsessive-Compulsive Disorder)</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    value={parameters.ocdDiagnosisDate?.value || null}
                                                    onChange={(date) => valueChanged(parameters.ocdDiagnosisDate, date)}
                                                    style={{width: '100%', marginTop: 8}}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Are you awaiting any of the following for this condition?',
                                                    name: 'ocdCondition',
                                                    options: fieldOptions.ocdCondition.options,
                                                    ...parameters.ocdCondition,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ocdTreatment"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.ocdTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.ocdTreatment, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ocdEvaluations"
                                                label="Provide details on all pending evaluations and treatment related to this condition."
                                                value={parameters.ocdEvaluations?.value}
                                                onChange={(value) => valueChanged(parameters.ocdEvaluations, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.PagetsDisease.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <Typography variant="h6">Paget's Disease</Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    value={parameters.pagetDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.pagetDiagnosisDate, date)
                                                    }
                                                    style={{width: '100%', marginTop: 8}}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Apart from your regular follow ups for this condition are you awaiting further?',
                                                    name: 'pagetCondition',
                                                    options: fieldOptions.pagetCondition.options,
                                                    ...parameters.pagetCondition,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'pagetSymptoms',
                                                    label: 'Are you currently experiencing symptoms or taking medication due to this condition?',
                                                    ...parameters.pagetSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="pagetBones"
                                                label="How many bones are affected by this condition?"
                                                value={parameters.pagetBones?.value}
                                                onChange={(value) => valueChanged(parameters.pagetBones, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'pagetComplications',
                                                    label: 'Do you have any ongoing complications and/or limitations due to this condition?',
                                                    ...parameters.pagetComplications,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="pagetComplicationDetails"
                                                label="Provide details of complications or limitations."
                                                value={parameters.pagetComplicationDetails?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.pagetComplicationDetails, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="pagetSymptomDetails"
                                                label="Provide details of your symptoms to include number of affected bones and treatment received."
                                                value={parameters.pagetSymptomDetails?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.pagetSymptomDetails, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.BladderInfection.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Bladder Infection</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.bladderinfectionDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.bladderinfectionDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'bladderinfectionPendingTreatment',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?',
                                                    ...parameters.bladderinfectionPendingTreatment,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="bladderinfectionTreatmentPlan"
                                                label="Provide details on all pending evaluations and treatment related to this condition., Provide details on all pending evaluations and treatment related to this condition. "
                                                value={parameters.bladderinfectionTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.bladderinfectionTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.KidneyDisorderKidneyDisease.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Kidney Disorder / Kidney Disease</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.kidneydiseaseDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.kidneydiseaseDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'kidneydiseaseDialysis',
                                                    label: 'Are you on dialysis ',
                                                    ...parameters.kidneydiseaseDialysis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="kidneydiseaseTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?, Provide details to include the type of surgery and date it was completed (mm/yy)."
                                                value={parameters.kidneydiseaseTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.kidneydiseaseTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.RestlessLegSyndromeRLS.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Restless Leg Syndrome (RLS)</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.rlsDiagnosisDate?.value || null}
                                                    onChange={(date) => valueChanged(parameters.rlsDiagnosisDate, date)}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.BacterialInfection.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Bacterial Infection</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.bacterialinfectionDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.bacterialinfectionDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.CysticFibrosis.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Cystic Fibrosis</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'cysticfibrosisDiagnosis',
                                                    label: 'Are you diagnosed with CF? ',
                                                    ...parameters.cysticfibrosisDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.cysticfibrosisDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.cysticfibrosisDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Nausea.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Nausea</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.nauseaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.nauseaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.TourettesSyndrome.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Tourette's Syndrome</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.tourettesDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.tourettesDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Malaria.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Malaria</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.malariaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.malariaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="malariaEpisodes"
                                                label="Have you had more than one episode of this condition? "
                                                value={parameters.malariaEpisodes?.value}
                                                onChange={(value) => valueChanged(parameters.malariaEpisodes, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'malariaSymptoms',
                                                    label: 'Was fever the only symptom you experienced?',
                                                    ...parameters.malariaSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'malariaRecovery',
                                                    label: 'Have you had a full recovery with no complications?',
                                                    ...parameters.malariaRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.HPV.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>HPV</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.hpvDianosisDate?.value}
                                                    onChange={(date) => valueChanged(parameters.hpvDianosisDate, date)}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hpvTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition? "
                                                value={parameters.hpvTreatmentPlan?.value}
                                                onChange={(value) => valueChanged(parameters.hpvTreatmentPlan, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'hpvCurrentSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition? ',
                                                    ...parameters.hpvCurrentSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Emphysema.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Emphysema</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.emphysemaDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.emphysemaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'emphysemaResolved',
                                                    label: 'Has your emphysema been resolved? ',
                                                    ...parameters.emphysemaResolved,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="emphysemaTreatment"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.emphysemaTreatment?.value}
                                                onChange={(value) => valueChanged(parameters.emphysemaTreatment, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="emphysemaTreatmentPlan"
                                                label="Provide details on steroid therapy., Are you currently experiencing symptoms related to this condition?, Provide details of your symptoms. "
                                                value={parameters.emphysemaTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.emphysemaTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.TardiveDyskinesia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Tardive Dyskinesia</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.tardiveDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.tardiveDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Acne.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Acne</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.acneDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.acneDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.FungalInfection.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Fungal Infection</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.fungalinfectionDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.fungalinfectionDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.BirthControl.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Birth Control</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you prescribed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.birthcontrolPrescribedDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.birthcontrolPrescribedDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Myelofibrosis.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Myelofibrosis</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.myelofibrosisDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.myelofibrosisDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.HormoneReplacementTherapy.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Hormone Replacement Therapy</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.hormonereplacementDiagnoisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.hormonereplacementDiagnoisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Endometriosis.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Endometriosis</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.endometriosisDiagnoisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.endometriosisDiagnoisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'endometriosisPendingDiangosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery? ',
                                                    ...parameters.endometriosisPendingDiangosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="endometriosisTreatmentPlan"
                                                label="Provide details on all pending evaluations and treatment related to this condition. "
                                                value={parameters.endometriosisTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.endometriosisTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Hyperkalemia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Hyperkalemia</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.hyperkalemiaDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.hyperkalemiaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Do you have a history of diabetes, pre-diabetes, or high blood sugar? ',
                                                    name: 'hyperkalemiaElevatedSugar',
                                                    options: fieldOptions.hyperkalemiaElevatedSugar.options,
                                                    ...parameters.hyperkalemiaElevatedSugar,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'hyperkalemiaHemoglobinTest',
                                                    label: 'Have you had your Hemoglobin A1c (HbA1c) tested within the last 12 months and do you know the results? ',
                                                    ...parameters.hyperkalemiaHemoglobinTest,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hyperkalemiaHbA1C"
                                                label="What was your most recent HbA1c reading?"
                                                value={parameters.hyperkalemiaHbA1C?.value}
                                                onChange={(value) => valueChanged(parameters.hyperkalemiaHbA1C, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hyperkalemiaRecentEpisode"
                                                label="When was your last episode of hypoglycemia? "
                                                value={parameters.hyperkalemiaRecentEpisode?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.hyperkalemiaRecentEpisode, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hyperkalemiaTreatmentPlan"
                                                label="Provide details on treatment received and date of diagnosis (mm/yy). "
                                                value={parameters.hyperkalemiaTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.hyperkalemiaTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.PanicDisorder.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Panic Disorder</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.panicdisorderDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.panicdisorderDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="panicdisorderTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition? "
                                                value={parameters.panicdisorderTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.panicdisorderTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'panicdisorderCounseling',
                                                    label: 'Have you ever been referred for or received inpatient psychotherapy or counseling? ',
                                                    ...parameters.panicdisorderCounseling,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="panicDisorderSymptoms"
                                                label="Have you lost more than 2 weeks worth of work or school due to this condition? Have you ever been hospitalized due to this condition, attempted suicide or had suicidal thoughts? "
                                                value={parameters.panicDisorderSymptoms?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.panicDisorderSymptoms, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.MultipleMyeloma.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Multiple Myeloma</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.multiplemyelomaDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.multiplemyelomaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="multiplemyelomaTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition? "
                                                value={parameters.multiplemyelomaTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.multiplemyelomaTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'multiplemyelomaBloodWork',
                                                    label: 'In the last 12 months have you had labs/blood work completed for this condition? ',
                                                    ...parameters.multiplemyelomaBloodWork,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'multiplemyelomaRecovery',
                                                    label: 'Have you had a full recovery with no complications?',
                                                    ...parameters.multiplemyelomaRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Constipation.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Constipation</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.constipationDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.constipationDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'constipationPendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?',
                                                    ...parameters.constipationPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="constipationTreatmentPlan"
                                                label="Was this condition caused by a common stomach flu, food poisoning or change in your diet?, Provide details to include the cause of the condition and treatment received. "
                                                value={parameters.constipationTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.constipationTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.OveractiveBladder.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Overactive Bladder</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.overactivebladderDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.overactivebladderDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="overactivebladderTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition? "
                                                value={parameters.overactivebladderTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.overactivebladderTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'overactivebladderSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition? ',
                                                    ...parameters.overactivebladderSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.OpioidEmergency.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Opioid Emergency</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.opioidDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.opioidDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'opioidTreament',
                                                    label: 'Have you ever sought treatment due to substance abuse or detoxification? ',
                                                    ...parameters.opioidTreament,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'opioidDrugFree',
                                                    label: 'Are you now drug-free? ',
                                                    ...parameters.opioidDrugFree,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="opioidTreatmentDetails"
                                                label="Have you ever consulted with a counselor or received treatment for alcohol use? Provide details on treatment received and date treatment was completed (mm/yy). "
                                                value={parameters.opioidTreatmentDetails?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.opioidTreatmentDetails, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Are you currently an active member of ?',
                                                    name: 'opioidMember',
                                                    options: fieldOptions.opioidMember.options,
                                                    ...parameters.opioidMember,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Neutropenia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Neutropenia</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.neutropeniaDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.neutropeniaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="neutropeniaTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.neutropeniaTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.neutropeniaTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'neutropeniaLabWork',
                                                    label: 'In the last 12 months have you had labs/blood work completed for this condition?',
                                                    ...parameters.neutropeniaLabWork,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'neutropeniaRecovery',
                                                    label: 'Have you had a full recovery with no complications?',
                                                    ...parameters.neutropeniaRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.IdiopathicPulmonaryFibrosisIPF.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Idiopathic Pulmonary Fibrosis (IPF)</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.fibrosisDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.fibrosisDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="fibrosisTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.fibrosisTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.fibrosisTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'fibrosisCurrentSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.fibrosisCurrentSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.WeightLossObesityGastric.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Weight Loss / Obesity/Gastric</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When did you have gastric surgery?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.gastricbypassSurgeryDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.gastricbypassSurgeryDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.InfluenzaFlu.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Influenza / Flu</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.fluDiagnosisDate?.value}
                                                    onChange={(date) => valueChanged(parameters.fluDiagnosisDate, date)}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'fluCurrentSymptoms',
                                                    label: 'Are you currently under treatment or experiencing symptoms related to this condition?',
                                                    ...parameters.fluCurrentSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'fluRecovery',
                                                    label: 'Have you had a full recovery with no complications?',
                                                    ...parameters.fluRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.PostTraumaticStressDisorderPTSD.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Post Traumatic Stress Disorder (PTSD)</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.ptsdDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.ptsdDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'What is the severity?',
                                                    name: 'ptsdSeverity',
                                                    options: fieldOptions.ptsdSeverity.options,
                                                    ...parameters.ptsdSeverity,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'ptsdInpatient',
                                                    label: 'Have you ever been referred for or received inpatient treatment for this condition?',
                                                    ...parameters.ptsdInpatient,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="Provide details to frequency, duration and dates (mm/yy)"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.ptsdInpatientDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.ptsdInpatientDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'ptsdSideEffects',
                                                    label: 'Have you ever been hospitalized due to this condition, attempted suicide or had suicidal thoughts?',
                                                    ...parameters.ptsdSideEffects,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="ptsdTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.ptsdTreatmentPlan?.value}
                                                onChange={(value) => valueChanged(parameters.ptsdTreatmentPlan, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Hypokalemia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Hypokalemia</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.hypokalemiaDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.hypokalemiaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Do you have a history of diabetes, pre-diabetes, or high blood sugar? ',
                                                    name: 'hypokalemiaElevatedSugar',
                                                    options: fieldOptions.hypokalemiaElevatedSugar.options,
                                                    ...parameters.hypokalemiaElevatedSugar,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'hypokalemiaHemoglobinTest',
                                                    label: 'Have you had your Hemoglobin A1c (HbA1c) tested within the last 12 months and do you know the results?',
                                                    ...parameters.hypokalemiaHemoglobinTest,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hypokalemiaHbA1C"
                                                label="What was your most recent HbA1c reading?"
                                                value={parameters.hypokalemiaHbA1C?.value}
                                                onChange={(value) => valueChanged(parameters.hypokalemiaHbA1C, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hypokalemiaRecentEpisode"
                                                label="When was your last episode of hypoglycemia?"
                                                value={parameters.hypokalemiaRecentEpisode?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.hypokalemiaRecentEpisode, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hypokalemiaTreatmentPlan"
                                                label="Provide details on treatment received and date of diagnosis (mm/yy)."
                                                value={parameters.hypokalemiaTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.hypokalemiaTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Congestion.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Congestion</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.congestionDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.congestionDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.ExcessiveUnderarmSweating.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Excessive Underarm Sweating</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.excessiveunderarmsweatingDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(
                                                            parameters.excessiveunderarmsweatingDiagnosisDate,
                                                            date,
                                                        )
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Tumor.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Tumor</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.tumorDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.tumorDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Have you ever been told the tumor was?',
                                                    name: 'tumorDiagnosisType',
                                                    options: fieldOptions.tumorDiagnosisType.options,
                                                    ...parameters.tumorDiagnosisType,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'tumorRemoval',
                                                    label: 'Have you had the tumor removed?',
                                                    ...parameters.tumorRemoval,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="tumorTreatmentPlan"
                                                label="Provide details to include date of diagnosis and treatment received."
                                                value={parameters.tumorTreatmentPlan?.value}
                                                onChange={(value) => valueChanged(parameters.tumorTreatmentPlan, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.HeartArrythmia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Heart Arrythmia</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.heartarrythmiaDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.heartarrythmiaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="heartarrythmiaEpisode"
                                                label="How many years since your last episode?"
                                                value={parameters.heartarrythmiaEpisode?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.heartarrythmiaEpisode, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="heartarrythmiaTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.heartarrythmiaTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.heartarrythmiaTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'heartarrythmiaAblation',
                                                    label: 'Have you had ablation?',
                                                    ...parameters.heartarrythmiaAblation,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'heartarrythmiaSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.heartarrythmiaSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Shingles.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Shingles</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.shinglesDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.shinglesDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'shinglesComplications',
                                                    label: 'Have you had any complications associated with this condition such as neuropathy, neuralgia, meningitis or eye involvement?',
                                                    ...parameters.shinglesComplications,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="shinglesComplicationsExplained"
                                                label="Provide details in regards to the complications, including diagnosis and treatment received"
                                                value={parameters.shinglesComplicationsExplained?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.shinglesComplicationsExplained, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.ChronicDryEye.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Chronic Dry Eye</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.chornicdryeyeDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.chornicdryeyeDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'chornicdryeyePhysicianConsult',
                                                    label: 'Have you consulted with any other physician for this other than your personal physician?',
                                                    ...parameters.chornicdryeyePhysicianConsult,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.TetanusLockjaw.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Tetanus (Lockjaw)</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.tetanusDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.tetanusDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.ErectileDysfunction.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Erectile Dysfunction</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.erectiledysfunctionDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.erectiledysfunctionDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'erectilediagnosisPhysicianConsult',
                                                    label: 'Have you consulted with any other physician for this other than your personal physician?',
                                                    ...parameters.erectilediagnosisPhysicianConsult,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Transplant.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Transplant</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'transplantDiagnosis',
                                                    label: 'Have you had an organ transplant?',
                                                    ...parameters.transplantDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.transplantDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.transplantDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="transplantTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.transplantTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.transplantTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Cough.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Cough</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.coughDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.coughDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'caughCause',
                                                    label: 'Was this condition caused by a common cold, allergies, laryngitis or overuse?',
                                                    ...parameters.caughCause,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="coughTreatmentPlan"
                                                label="Provide details to include the cause of the condition and treatment received."
                                                value={parameters.coughTreatmentPlan?.value}
                                                onChange={(value) => valueChanged(parameters.coughTreatmentPlan, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'coughRecovery',
                                                    label: 'Have you had a full recovery with no complications?',
                                                    ...parameters.coughRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.CirrhosisoftheLiver.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Cirrhosis of the Liver</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'cirrhosisDiagnosis',
                                                    label: 'Have you been diagnosed with cirrhosis of the liver?',
                                                    ...parameters.cirrhosisDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.cirrhosisDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.cirrhosisDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.UrinaryRetention.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Urinary Retention</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.urinaryretentionDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.urinaryretentionDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'urinaryretentionPendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?',
                                                    ...parameters.urinaryretentionPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="urinaryretentionTreatmentPlan"
                                                label="Provide details on all pending evaluations and treatment related to this condition"
                                                value={parameters.urinaryretentionTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.urinaryretentionTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'urinaryretentionRecovery',
                                                    label: 'Have you had a full recovery with no complications?',
                                                    ...parameters.urinaryretentionRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Gallstones.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Gallstones</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.gallstonesDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.gallstonesDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'gallstonesPendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?',
                                                    ...parameters.gallstonesPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="gallstonesTreatmentPlan"
                                                label="Provide details on all pending evaluations and treatment related to this condition."
                                                value={parameters.gallstonesTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.gallstonesTreatmentPlan, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.SmokingCessationQuitSmoking.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Smoking Cessation / Quit Smoking</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.smokingcessationDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.smokingcessationDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.EatingDisorder.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Eating Disorder</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.eatingdisorderDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.eatingdisorderDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.ChronicHives.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Chronic Hives</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.chronichivesDiagnosiDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.chronichivesDiagnosiDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Polyps.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Polyps</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.polypDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.polypDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="polypLocation"
                                                label="Where were the polyp(s) located?"
                                                value={parameters.polypLocation?.value}
                                                onChange={(value) => valueChanged(parameters.polypLocation, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'polypPendingDiagnosis',
                                                    label: 'Are you awaiting any of the following for this condition: medical tests or investigations, test results or referral to hospital or surgery?',
                                                    ...parameters.polypPendingDiagnosis,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="polypTreatmentPlan"
                                                label="Provide details on all pending evaluations and treatment related to this condition."
                                                value={parameters.polypTreatmentPlan?.value}
                                                onChange={(value) => valueChanged(parameters.polypTreatmentPlan, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'polypBiopsyCompleted',
                                                    label: 'Has a biopsy been completed due to this condition?',
                                                    ...parameters.polypBiopsyCompleted,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Were the biopsy results?',
                                                    name: 'polypBiopsyResults',
                                                    options: fieldOptions.polypBiopsyResults.options,
                                                    ...parameters.polypBiopsyResults,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="polypBiopsySurgery"
                                                label="Has this condition been treated by surgery?"
                                                value={parameters.polypBiopsySurgery?.value}
                                                onChange={(value) => valueChanged(parameters.polypBiopsySurgery, value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'polypRecovery',
                                                    label: 'Have you had a full recovery with no complications?',
                                                    ...parameters.polypRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.ObstructiveAirwayDisease.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Obstructive Airway Disease</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.obstructiveairwaydiseaseDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(
                                                            parameters.obstructiveairwaydiseaseDiagnosisDate,
                                                            date,
                                                        )
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="obstructiveairwaydiseaseTreatmentPlan"
                                                label="What treatment was prescribed or completed for this condition?"
                                                value={parameters.obstructiveairwaydiseaseTreatmentPlan?.value}
                                                onChange={(value) =>
                                                    valueChanged(
                                                        parameters.obstructiveairwaydiseaseTreatmentPlan,
                                                        value,
                                                    )
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'obstructiveairwaydiseaseRecovery',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.obstructiveairwaydiseaseRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Cataplexy.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Cataplexy</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.cataplexyDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.cataplexyDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.PleuralMesothelioma.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Pleural Mesothelioma</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.mesotheliomaDiagnosisDate?.value}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.mesotheliomaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'mesotheliomaSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.mesotheliomaSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="mesotheliomaLastEpisode"
                                                label="When was your last episode of this condition?"
                                                value={parameters.mesotheliomaLastEpisode?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.mesotheliomaLastEpisode, value)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'mesotheliomaRecovery',
                                                    label: 'Has the condition fully resolved with no complications?',
                                                    ...parameters.mesotheliomaRecovery,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
});

export default HealthRelated;
